import { useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { complianceApi, type TDSSectionRecord, type TdsTcsSettings } from '@/api/compliance.api';
import OtpAuthModal from '../components/OtpAuthModal';

// ─── Form model — everything the Save button persists ─────────────────────────
interface SettingsForm {
  tan: string;
  enableTcs: boolean;
  tcsThreshold: number;
  tcsRate: number;
  tcsRateWithoutPan: number;
  autoTdsOnPayments: boolean;
  autoTcsOnSales: boolean;
  remindDays: number;
  environment: 'sandbox' | 'production';
  gstUsername: string;
  gstin: string;
  clientId: string;
  clientSecret: string;
  email: string;
  serverIp: string;
  emailAlertsEnabled: boolean;
  emailRecipients: string;
}

const EMPTY_FORM: SettingsForm = {
  tan: '',
  enableTcs: true,
  tcsThreshold: 5000000,
  tcsRate: 0.1,
  tcsRateWithoutPan: 1.0,
  autoTdsOnPayments: true,
  autoTcsOnSales: true,
  remindDays: 3,
  environment: 'sandbox',
  gstUsername: '',
  gstin: '',
  clientId: '',
  clientSecret: '',
  email: '',
  serverIp: '',
  emailAlertsEnabled: true,
  emailRecipients: '',
};

const fromApi = (s: TdsTcsSettings): SettingsForm => ({
  tan: s.tan || '',
  enableTcs: s.enableTcs,
  tcsThreshold: Number(s.tcsThreshold),
  tcsRate: Number(s.tcsRate),
  tcsRateWithoutPan: Number(s.tcsRateWithoutPan),
  autoTdsOnPayments: s.autoTdsOnPayments,
  autoTcsOnSales: s.autoTcsOnSales,
  remindDays: Number(s.remindDays) || 3,
  environment: s.environment === 'production' ? 'production' : 'sandbox',
  gstUsername: s.gstUsername || '',
  gstin: s.gstin || '',
  clientId: s.clientId || '',
  clientSecret: s.clientSecret || '',
  email: s.email || '',
  serverIp: s.serverIp || '',
  emailAlertsEnabled: s.emailAlertsEnabled,
  emailRecipients: s.emailRecipients || '',
});

const TAN_RE = /^[A-Z]{4}[0-9]{5}[A-Z]$/;
const GSTIN_RE = /^[0-9]{2}[A-Z0-9]{13}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(f: SettingsForm): Partial<Record<keyof SettingsForm, string>> {
  const e: Partial<Record<keyof SettingsForm, string>> = {};
  if (f.tan && !TAN_RE.test(f.tan)) e.tan = 'Format: 4 letters, 5 digits, 1 letter (e.g. MUMC12345A)';
  if (f.gstin && !GSTIN_RE.test(f.gstin)) e.gstin = 'GSTIN must be 15 characters starting with the 2-digit state code';
  if (f.email && !EMAIL_RE.test(f.email)) e.email = 'Enter a valid email address';
  if (f.enableTcs) {
    if (!Number.isFinite(f.tcsThreshold) || f.tcsThreshold < 0) e.tcsThreshold = 'Threshold must be 0 or more';
    if (!Number.isFinite(f.tcsRate) || f.tcsRate < 0 || f.tcsRate > 100) e.tcsRate = 'Rate must be 0–100%';
    if (!Number.isFinite(f.tcsRateWithoutPan) || f.tcsRateWithoutPan < 0 || f.tcsRateWithoutPan > 100) e.tcsRateWithoutPan = 'Rate must be 0–100%';
  }
  if (f.emailAlertsEnabled) {
    const bad = f.emailRecipients.split(',').map(x => x.trim()).filter(x => x && !EMAIL_RE.test(x));
    if (bad.length) e.emailRecipients = `Invalid: ${bad.join(', ')}`;
  }
  return e;
}

const formatINR = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const inWords = (n: number) => {
  if (n >= 1e7) return `${+(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `${+(n / 1e5).toFixed(2)} Lakh`;
  if (n >= 1e3) return `${+(n / 1e3).toFixed(2)} K`;
  return String(n);
};

const NAV = [
  { id: 'tan', label: 'Company TAN', icon: 'ri-building-2-line' },
  { id: 'sections', label: 'TDS Sections', icon: 'ri-list-check-2' },
  { id: 'tcs', label: 'TCS Configuration', icon: 'ri-exchange-funds-line' },
  { id: 'automation', label: 'Automation', icon: 'ri-robot-2-line' },
  { id: 'api', label: 'GST Filing API', icon: 'ri-key-2-line' },
  { id: 'alerts', label: 'Notifications', icon: 'ri-notification-3-line' },
];

// ─── Small presentational helpers ────────────────────────────────────────────
function Toggle({ checked, onChange, disabled, size = 'md' }: { checked: boolean; onChange: () => void; disabled?: boolean; size?: 'sm' | 'md' }) {
  const dims = size === 'sm' ? 'w-9 h-5' : 'w-11 h-6';
  const knob = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  const shift = size === 'sm' ? (checked ? 'translate-x-4' : 'translate-x-0.5') : (checked ? 'translate-x-5' : 'translate-x-0.5');
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-block shrink-0 p-0 ${dims} rounded-full transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${checked ? 'bg-indigo-600' : 'bg-slate-200'}`}
    >
      {/* left-0 is required: buttons center their content, so without it the knob starts mid-track */}
      <span className={`absolute left-0 top-0.5 ${knob} bg-white rounded-full shadow transition-transform ${shift}`} />
    </button>
  );
}

function Card({ id, icon, title, subtitle, right, children }: { id: string; icon: string; title: string; subtitle?: string; right?: ReactNode; children: ReactNode }) {
  return (
    <section id={id} className="bg-white border border-slate-200 rounded-xl shadow-sm scroll-mt-24">
      <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <i className={`${icon} text-lg`} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">{title}</h3>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Field({ label, hint, error, children, required }: { label: string; hint?: ReactNode; error?: string; children: ReactNode; required?: boolean }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 block mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1"><i className="ri-error-warning-line" />{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-slate-400 mt-1">{hint}</p>
      ) : null}
    </div>
  );
}

const inputCls = (err?: string) =>
  `w-full h-10 px-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 transition-colors ${err
    ? 'border-red-300 focus:border-red-400 focus:ring-red-500/15'
    : 'border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/15'
  }`;

function SwitchRow({ title, desc, checked, onChange }: { title: string; desc: string; checked: boolean; onChange: () => void }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div>
        <p className="text-sm font-medium text-slate-800">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
}

// ─── Section row with inline edit ────────────────────────────────────────────
type SectionDraft = Pick<TDSSectionRecord, 'description' | 'threshold' | 'annualThreshold' | 'rateIndividual' | 'rateCompany'>;

function SectionRow({
  s, busy, onToggle, onSave,
}: {
  s: TDSSectionRecord;
  busy: boolean;
  onToggle: () => void;
  onSave: (draft: SectionDraft) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<SectionDraft>(s);

  useEffect(() => { if (!editing) setDraft(s); }, [s, editing]);

  const numCell = (key: keyof SectionDraft, opts: { pct?: boolean }) => (
    <input
      type="number"
      min={0}
      max={opts.pct ? 100 : undefined}
      step={opts.pct ? '0.01' : '1'}
      value={draft[key] as number}
      onChange={e => setDraft(d => ({ ...d, [key]: e.target.value === '' ? ('' as unknown as number) : Number(e.target.value) }))}
      className="w-full h-8 px-2 rounded-md border border-slate-200 text-xs text-right focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15"
    />
  );

  const save = async () => {
    if (await onSave(draft)) setEditing(false);
  };

  return (
    <tr className={`border-b border-slate-100 last:border-0 transition-colors ${editing ? 'bg-indigo-50/40' : s.isActive ? 'hover:bg-slate-50' : 'bg-slate-50/60 hover:bg-slate-50'}`}>
      <td className="py-2.5 px-4">
        <Toggle size="sm" checked={s.isActive} onChange={onToggle} disabled={busy || editing} />
      </td>
      <td className="py-2.5 px-4">
        <span className={`inline-flex px-2 py-0.5 rounded-md font-mono text-xs font-bold ${s.isActive ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-500'}`}>
          {s.section}
        </span>
      </td>
      <td className="py-2.5 px-4 min-w-[200px]">
        {editing ? (
          <input
            value={draft.description}
            onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
            className="w-full h-8 px-2 rounded-md border border-slate-200 text-xs focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15"
          />
        ) : (
          <span className={`text-xs ${s.isActive ? 'text-slate-700' : 'text-slate-400'}`}>{s.description}</span>
        )}
      </td>
      <td className="py-2.5 px-4 text-right text-xs text-slate-600 w-[120px]">
        {editing ? numCell('threshold', {}) : formatINR(s.threshold)}
      </td>
      <td className="py-2.5 px-4 text-right text-xs text-slate-600 w-[120px]">
        {editing ? numCell('annualThreshold', {}) : formatINR(s.annualThreshold)}
      </td>
      <td className="py-2.5 px-4 text-right text-xs text-slate-600 w-[90px]">
        {editing ? numCell('rateIndividual', { pct: true }) : `${Number(s.rateIndividual)}%`}
      </td>
      <td className="py-2.5 px-4 text-right text-xs font-semibold text-slate-700 w-[90px]">
        {editing ? numCell('rateCompany', { pct: true }) : `${Number(s.rateCompany)}%`}
      </td>
      <td className="py-2.5 px-3 text-right w-[84px]">
        {editing ? (
          <div className="flex items-center justify-end gap-1">
            <button type="button" onClick={save} disabled={busy} title="Save"
              className="w-7 h-7 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 cursor-pointer flex items-center justify-center">
              {busy ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-check-line text-sm" />}
            </button>
            <button type="button" onClick={() => setEditing(false)} disabled={busy} title="Cancel"
              className="w-7 h-7 rounded-md border border-slate-200 text-slate-500 hover:bg-white cursor-pointer flex items-center justify-center">
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => setEditing(true)} title="Edit rates & thresholds"
            className="w-7 h-7 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 cursor-pointer inline-flex items-center justify-center">
            <i className="ri-pencil-line text-sm" />
          </button>
        )}
      </td>
    </tr>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function TDSSettingsPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<SettingsForm>(EMPTY_FORM);
  const [saved, setSaved] = useState<SettingsForm>(EMPTY_FORM);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const [sectionsList, setSectionsList] = useState<TDSSectionRecord[]>([]);
  const [busySection, setBusySection] = useState<string | null>(null);
  const [sectionQuery, setSectionQuery] = useState('');
  const [sectionFilter, setSectionFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const [session, setSession] = useState<TdsTcsSettings['session']>({ status: 'PENDING', active: false, expiresAt: null });
  const [showSecret, setShowSecret] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [verifyingGstin, setVerifyingGstin] = useState(false);

  const set = <K extends keyof SettingsForm>(key: K, value: SettingsForm[K]) => setForm(f => ({ ...f, [key]: value }));

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [sectionsRes, settingsRes] = await Promise.all([
        complianceApi.getTDSSections().catch(() => null),
        complianceApi.getTdsTcsSettings().catch(() => null),
      ]);

      if (sectionsRes?.success && Array.isArray(sectionsRes.data)) {
        setSectionsList(sectionsRes.data);
      }

      if (settingsRes?.success && settingsRes.data) {
        const next = fromApi(settingsRes.data);
        setForm(next);
        setSaved(next);
        setSession(settingsRes.data.session);
        setUpdatedAt(settingsRes.data.updatedAt || null);
      } else {
        setLoadError(settingsRes?.message || 'Could not load TDS/TCS settings');
      }
    } catch (err: any) {
      setLoadError(err?.message || 'Could not load TDS/TCS settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const errors = useMemo(() => validate(form), [form]);
  const hasErrors = Object.keys(errors).length > 0;
  const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(saved), [form, saved]);
  const credsDirty = ['gstUsername', 'gstin', 'clientId', 'clientSecret', 'email', 'environment', 'serverIp']
    .some(k => form[k as keyof SettingsForm] !== saved[k as keyof SettingsForm]);

  // Warn before leaving with unsaved edits
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleSave = useCallback(async () => {
    setTouched(true);
    if (hasErrors) {
      toast.error(Object.values(errors)[0] || 'Fix the highlighted fields');
      return;
    }
    setSaving(true);
    try {
      const res = await complianceApi.updateTdsTcsSettings({
        ...form,
        stateCd: form.gstin ? form.gstin.substring(0, 2) : '',
      });
      if (res?.success) {
        const next = res.data ? fromApi(res.data) : form;
        setForm(next);
        setSaved(next);
        if (res.data) {
          setSession(res.data.session);
          setUpdatedAt(res.data.updatedAt || new Date().toISOString());
        }
        setTouched(false);
        toast.success('TDS / TCS settings saved');
      } else {
        toast.error(res?.message || 'Failed to save settings');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [form, errors, hasErrors, toast]);

  // Ctrl/Cmd + S saves
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (isDirty && !saving) handleSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDirty, saving, handleSave]);

  const updateSection = async (s: TDSSectionRecord, changes: Parameters<typeof complianceApi.updateTDSSection>[1]) => {
    setBusySection(s.id);
    try {
      const res = await complianceApi.updateTDSSection(s.id, changes);
      if (res?.success && res.data) {
        const updated = res.data;
        setSectionsList(prev => prev.map(x => (x.id === s.id ? { ...x, ...updated } : x)));
        return true;
      }
      toast.error(res?.message || 'Failed to update section');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update section');
    } finally {
      setBusySection(null);
    }
    return false;
  };

  // Flip immediately, roll back if the server rejects it
  const toggleSectionActive = async (s: TDSSectionRecord) => {
    const next = !s.isActive;
    setSectionsList(prev => prev.map(x => (x.id === s.id ? { ...x, isActive: next } : x)));
    const ok = await updateSection(s, { isActive: next });
    if (!ok) {
      setSectionsList(prev => prev.map(x => (x.id === s.id ? { ...x, isActive: s.isActive } : x)));
    } else {
      toast.success(`Section ${s.section} ${next ? 'activated' : 'deactivated'}`);
    }
  };

  const saveSectionDraft = async (s: TDSSectionRecord, d: SectionDraft) => {
    const nums = [d.threshold, d.annualThreshold, d.rateIndividual, d.rateCompany].map(Number);
    if (!String(d.description || '').trim()) { toast.error('Description cannot be empty'); return false; }
    if (nums.some(n => !Number.isFinite(n) || n < 0)) { toast.error('Thresholds and rates must be 0 or more'); return false; }
    if (Number(d.rateIndividual) > 100 || Number(d.rateCompany) > 100) { toast.error('Rates cannot exceed 100%'); return false; }
    const ok = await updateSection(s, {
      description: d.description.trim(),
      threshold: Number(d.threshold),
      annualThreshold: Number(d.annualThreshold),
      rateIndividual: Number(d.rateIndividual),
      rateCompany: Number(d.rateCompany),
    });
    if (ok) toast.success(`Section ${s.section} updated`);
    return ok;
  };

  const handleVerifyGstin = async () => {
    if (!GSTIN_RE.test(form.gstin)) {
      toast.error('Enter a valid 15-character GSTIN first');
      return;
    }
    if (credsDirty) {
      toast.warning('Save your API credentials first — verification uses the saved credentials');
      return;
    }
    setVerifyingGstin(true);
    try {
      const res = await complianceApi.verifyTdsGstin(form.gstin);
      if (res?.success && res.data) {
        toast.success(`Credentials work ✓ ${res.data.tradeName || res.data.legalName || res.data.gstin} — ${res.data.status}`);
      } else {
        toast.error(res?.message || 'GSTIN verification failed');
      }
    } catch (err: any) {
      toast.error(err?.message || 'GSTIN verification failed');
    } finally {
      setVerifyingGstin(false);
    }
  };

  const openAuth = () => {
    if (credsDirty) {
      toast.warning('Save your API credentials first — authentication uses the saved credentials');
      return;
    }
    if (!form.clientId || !form.clientSecret || !form.gstUsername || !form.gstin) {
      toast.error('Fill in GST Username, GSTIN, Client ID and Client Secret, then save');
      return;
    }
    setShowOtpModal(true);
  };

  const switchEnvironment = (env: SettingsForm['environment']) => {
    if (env === form.environment) return;
    if (env === 'production' && !window.confirm('Switch to PRODUCTION? GSTR-7/GSTR-8 filings will be submitted to GSTN for real and cannot be undone.')) return;
    set('environment', env);
  };

  const filteredSections = useMemo(() => {
    const q = sectionQuery.trim().toLowerCase();
    return sectionsList.filter(s =>
      (sectionFilter === 'all' || (sectionFilter === 'active' ? s.isActive : !s.isActive)) &&
      (!q || s.section.toLowerCase().includes(q) || (s.description || '').toLowerCase().includes(q))
    );
  }, [sectionsList, sectionQuery, sectionFilter]);

  const activeCount = sectionsList.filter(s => s.isActive).length;
  const recipients = form.emailRecipients.split(',').map(x => x.trim()).filter(Boolean);
  const showErr = (k: keyof SettingsForm) => (touched || form[k] !== saved[k] ? errors[k] : undefined);

  // Worked example for the TCS card: a ₹10L invoice to a customer already past the threshold
  const exampleSale = 1000000;
  const exTcs = (exampleSale * (Number(form.tcsRate) || 0)) / 100;
  const exTcsNoPan = (exampleSale * (Number(form.tcsRateWithoutPan) || 0)) / 100;

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-2 text-slate-600 text-sm font-medium">
            <i className="ri-loader-4-line animate-spin text-xl text-indigo-600" />
            Loading TDS / TCS settings…
          </div>
        </div>
      </AppLayout>
    );
  }

  if (loadError) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-3 text-center px-4">
          <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center">
            <i className="ri-error-warning-line text-2xl" />
          </div>
          <p className="text-sm font-semibold text-slate-800">{loadError}</p>
          <div className="flex gap-2">
            <button onClick={() => navigate('/compliance/tds')} className="h-9 px-4 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 cursor-pointer">Back</button>
            <button onClick={load} className="h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer">Retry</button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const statusCards = [
    {
      label: 'Company TAN',
      value: saved.tan || 'Not set',
      ok: TAN_RE.test(saved.tan),
      icon: 'ri-building-2-line',
      href: 'tan',
      mono: Boolean(saved.tan),
    },
    {
      label: 'TDS Sections',
      value: `${activeCount} of ${sectionsList.length} active`,
      ok: activeCount > 0,
      icon: 'ri-list-check-2',
      href: 'sections',
    },
    {
      label: 'TCS on Sales',
      value: saved.enableTcs ? (saved.autoTcsOnSales ? `On · ${saved.tcsRate}%` : 'On · auto-calc off') : 'Off',
      ok: saved.enableTcs,
      icon: 'ri-exchange-funds-line',
      href: 'tcs',
    },
    {
      label: 'GSTN Session',
      value: session.active ? 'Active' : session.status === 'EXPIRED' ? 'Expired' : 'Not authenticated',
      ok: session.active,
      icon: 'ri-shield-keyhole-line',
      href: 'api',
    },
  ];

  return (
    <AppLayout>
      <div className="bg-slate-50 min-h-full">
        {/* Sticky header */}
        <div className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => navigate('/compliance/tds')}
                title="Back to TDS / TCS"
                className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-line text-lg" />
              </button>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-slate-800 truncate">TDS / TCS Settings</h1>
                <p className="text-xs text-slate-500 truncate">
                  {updatedAt ? `Last saved ${new Date(updatedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}` : 'Not saved yet — defaults shown'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {isDirty && (
                <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Unsaved changes
                </span>
              )}
              {isDirty && (
                <button onClick={() => { setForm(saved); setTouched(false); }} disabled={saving}
                  className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer disabled:opacity-50">
                  Discard
                </button>
              )}
              <button onClick={handleSave} disabled={saving || !isDirty}
                title="Save (Ctrl/⌘ + S)"
                className="flex items-center gap-2 h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-colors shadow-sm">
                {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-3-line" />}
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
          {/* Status overview */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {statusCards.map(c => (
              <a key={c.label} href={`#${c.href}`}
                className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 hover:border-indigo-300 hover:shadow-sm transition-all">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${c.ok ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                  <i className={`${c.icon} text-lg`} />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{c.label}</p>
                  <p className={`text-sm font-bold truncate ${c.ok ? 'text-slate-800' : 'text-amber-700'} ${c.mono ? 'font-mono tracking-wide' : ''}`}>{c.value}</p>
                </div>
              </a>
            ))}
          </div>

          <div className="flex gap-6">
            {/* Section nav */}
            <nav className="hidden lg:block w-52 shrink-0">
              <div className="sticky top-24 space-y-0.5">
                {NAV.map(n => (
                  <a key={n.id} href={`#${n.id}`}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-white hover:text-indigo-700 hover:shadow-sm transition-all">
                    <i className={`${n.icon} text-base text-slate-400`} />
                    {n.label}
                  </a>
                ))}
              </div>
            </nav>

            <div className="flex-1 min-w-0 space-y-5">
              {/* Company TAN */}
              <Card id="tan" icon="ri-building-2-line" title="Company TAN"
                subtitle="Quoted on every TDS challan, Form 16A certificate and quarterly return">
                <div className="max-w-sm">
                  <Field label="TAN (Tax Deduction Account Number)" error={showErr('tan')}
                    hint="10 characters issued by the Income Tax Department">
                    <div className="relative">
                      <input
                        type="text"
                        value={form.tan}
                        onChange={e => set('tan', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                        placeholder="MUMC12345A"
                        maxLength={10}
                        className={`${inputCls(showErr('tan'))} font-mono tracking-widest pr-10`}
                      />
                      {TAN_RE.test(form.tan) && (
                        <i className="ri-checkbox-circle-fill text-emerald-500 absolute right-3 top-1/2 -translate-y-1/2" />
                      )}
                    </div>
                  </Field>
                </div>
              </Card>

              {/* TDS Sections */}
              <Card id="sections" icon="ri-list-check-2" title="TDS Sections"
                subtitle="Active sections appear in the party master and are applied on purchase invoices. Changes save instantly."
                right={
                  <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2.5 py-1 rounded-full whitespace-nowrap">
                    {activeCount}/{sectionsList.length} active
                  </span>
                }>
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <div className="relative flex-1">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                    <input value={sectionQuery} onChange={e => setSectionQuery(e.target.value)}
                      placeholder="Search section or description…"
                      className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15" />
                  </div>
                  <div className="inline-flex p-0.5 bg-slate-100 rounded-lg self-start">
                    {(['all', 'active', 'inactive'] as const).map(f => (
                      <button key={f} type="button" onClick={() => setSectionFilter(f)}
                        className={`px-3 h-8 rounded-md text-xs font-medium capitalize cursor-pointer transition-colors ${sectionFilter === f ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>

                {sectionsList.length === 0 ? (
                  <div className="text-center py-10 text-sm text-slate-400">
                    <i className="ri-inbox-line text-2xl block mb-1" /> No TDS sections found
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50">
                          <th className="py-2.5 px-4 font-semibold">Active</th>
                          <th className="py-2.5 px-4 font-semibold">Section</th>
                          <th className="py-2.5 px-4 font-semibold">Description</th>
                          <th className="py-2.5 px-4 font-semibold text-right">Per txn</th>
                          <th className="py-2.5 px-4 font-semibold text-right">Annual</th>
                          <th className="py-2.5 px-4 font-semibold text-right">Indiv.</th>
                          <th className="py-2.5 px-4 font-semibold text-right">Company</th>
                          <th className="py-2.5 px-3" />
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSections.length === 0 ? (
                          <tr><td colSpan={8} className="py-8 text-center text-xs text-slate-400">No sections match</td></tr>
                        ) : filteredSections.map(s => (
                          <SectionRow
                            key={s.id}
                            s={s}
                            busy={busySection === s.id}
                            onToggle={() => toggleSectionActive(s)}
                            onSave={d => saveSectionDraft(s, d)}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-[11px] text-slate-400 mt-2">
                  Rate without PAN is doubled (min 20%) automatically under Section 206AA. Existing parties keep their saved rate until their section is re-selected in the party master.
                </p>
              </Card>

              {/* TCS */}
              <Card id="tcs" icon="ri-exchange-funds-line" title="TCS Configuration"
                subtitle="Tax collected from customers under Section 206C(1H) — added on top of the sales invoice"
                right={<Toggle checked={form.enableTcs} onChange={() => set('enableTcs', !form.enableTcs)} />}>
                {form.enableTcs ? (
                  <div className="space-y-5">
                    <Field label="Threshold per customer per financial year (₹)" error={showErr('tcsThreshold')}
                      hint={<>TCS applies on sales above <strong className="text-slate-600">{formatINR(form.tcsThreshold)}</strong> ({inWords(form.tcsThreshold)}) to one customer in a FY</>}>
                      <div className="relative max-w-xs">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                        <input type="number" min={0} value={Number.isNaN(form.tcsThreshold) ? '' : form.tcsThreshold}
                          onChange={e => set('tcsThreshold', e.target.value === '' ? NaN : Number(e.target.value))}
                          className={`${inputCls(showErr('tcsThreshold'))} pl-7`} />
                      </div>
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
                      <Field label="Rate — customer has PAN" error={showErr('tcsRate')} hint="Statutory rate: 0.1%">
                        <div className="relative">
                          <input type="number" step="0.01" min={0} max={100} value={Number.isNaN(form.tcsRate) ? '' : form.tcsRate}
                            onChange={e => set('tcsRate', e.target.value === '' ? NaN : Number(e.target.value))}
                            className={`${inputCls(showErr('tcsRate'))} pr-8`} />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                        </div>
                      </Field>
                      <Field label="Rate — no PAN (Sec 206CCA)" error={showErr('tcsRateWithoutPan')} hint="Statutory rate: 1.0%">
                        <div className="relative">
                          <input type="number" step="0.01" min={0} max={100} value={Number.isNaN(form.tcsRateWithoutPan) ? '' : form.tcsRateWithoutPan}
                            onChange={e => set('tcsRateWithoutPan', e.target.value === '' ? NaN : Number(e.target.value))}
                            className={`${inputCls(showErr('tcsRateWithoutPan'))} pr-8`} />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                        </div>
                      </Field>
                    </div>
                    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 flex items-start gap-2">
                      <i className="ri-calculator-line text-indigo-500 text-sm mt-px" />
                      <span>
                        Example: a {formatINR(exampleSale)} invoice to a customer already above the threshold collects{' '}
                        <strong className="text-slate-800">{formatINR(+exTcs.toFixed(2))}</strong> TCS with PAN, or{' '}
                        <strong className="text-slate-800">{formatINR(+exTcsNoPan.toFixed(2))}</strong> without PAN.
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 flex items-center gap-2">
                    <i className="ri-forbid-line text-slate-400" /> TCS is off — no TCS will be added to sales invoices.
                  </p>
                )}
              </Card>

              {/* Automation */}
              <Card id="automation" icon="ri-robot-2-line" title="Automation"
                subtitle="Control what the system calculates for you on new and edited invoices">
                <div className="divide-y divide-slate-100 -my-3">
                  <SwitchRow
                    title="Auto-calculate TDS on purchases"
                    desc="Apply the supplier's TDS section and rate on purchase invoices, deducted when the payment is recorded"
                    checked={form.autoTdsOnPayments}
                    onChange={() => set('autoTdsOnPayments', !form.autoTdsOnPayments)}
                  />
                  <SwitchRow
                    title="Auto-calculate TCS on sales"
                    desc={form.enableTcs ? 'Add TCS to sales invoices once a customer crosses the threshold' : 'TCS is disabled above — turn it on to use this'}
                    checked={form.autoTcsOnSales}
                    onChange={() => set('autoTcsOnSales', !form.autoTcsOnSales)}
                  />
                  <div className="flex items-center justify-between gap-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-slate-800">Deposit reminder</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Flag deposits as "Due Soon" on the TDS dashboard this many days before the 7th-of-month due date
                      </p>
                    </div>
                    <select
                      value={form.remindDays}
                      onChange={e => set('remindDays', Number(e.target.value))}
                      className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:border-indigo-500 cursor-pointer"
                    >
                      {[1, 2, 3, 5, 7, 10, 15].map(d => (
                        <option key={d} value={d}>{d} day{d > 1 ? 's' : ''} before</option>
                      ))}
                    </select>
                  </div>
                </div>
              </Card>

              {/* Whitebooks API */}
              <Card id="api" icon="ri-key-2-line" title="Whitebooks GST Return Filing API"
                subtitle="Used for GSTIN verification, GSTR-7 (TDS), GSTR-8 (TCS) and reconciliation. Separate from the E-Invoice / E-Way Bill credentials."
                right={
                  <div className="inline-flex p-0.5 bg-slate-100 rounded-lg shrink-0">
                    {(['sandbox', 'production'] as const).map(m => (
                      <button key={m} type="button" onClick={() => switchEnvironment(m)}
                        className={`px-3 h-8 rounded-md text-xs font-semibold capitalize cursor-pointer transition-colors ${form.environment === m
                          ? m === 'production' ? 'bg-red-600 text-white shadow-sm' : 'bg-white text-amber-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'}`}>
                        {m}
                      </button>
                    ))}
                  </div>
                }>
                {form.environment === 'production' ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 mb-5 flex items-start gap-2">
                    <i className="ri-error-warning-line text-sm" /> Production — GSTR-7/GSTR-8 filings go to GSTN for real and are irreversible.
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 mb-5 flex items-start gap-2">
                    <i className="ri-flask-line text-sm" /> Sandbox — test against Whitebooks' sandbox before switching to production.
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Company GSTIN" required error={showErr('gstin')}
                    hint={GSTIN_RE.test(form.gstin) ? `State code ${form.gstin.substring(0, 2)} (auto-detected)` : '15 characters'}>
                    <input type="text" value={form.gstin}
                      onChange={e => set('gstin', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                      placeholder="27AABCU9603R1ZX" maxLength={15}
                      className={`${inputCls(showErr('gstin'))} font-mono tracking-wide`} />
                  </Field>
                  <Field label="GST Username" required hint="GST portal username for this GSTIN">
                    <input type="text" value={form.gstUsername} onChange={e => set('gstUsername', e.target.value)}
                      placeholder="GST portal username" className={inputCls()} />
                  </Field>
                  <Field label="Client ID" required>
                    <input type="text" value={form.clientId} onChange={e => set('clientId', e.target.value)}
                      placeholder="Whitebooks Client ID" className={`${inputCls()} font-mono text-xs`} />
                  </Field>
                  <Field label="Client Secret" required>
                    <div className="relative">
                      <input type={showSecret ? 'text' : 'password'} value={form.clientSecret}
                        onChange={e => set('clientSecret', e.target.value)}
                        placeholder="••••••••" autoComplete="new-password"
                        className={`${inputCls()} pr-10 font-mono text-xs`} />
                      <button type="button" onClick={() => setShowSecret(!showSecret)} title={showSecret ? 'Hide' : 'Show'}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer">
                        <i className={showSecret ? 'ri-eye-off-line' : 'ri-eye-line'} />
                      </button>
                    </div>
                  </Field>
                  <Field label="Registered email" error={showErr('email')} hint="Email registered with Whitebooks — OTPs are sent here">
                    <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                      placeholder="you@company.com" className={inputCls(showErr('email'))} />
                  </Field>
                  <Field label="Server public IP" hint="Optional — leave blank to auto-detect">
                    <input type="text" value={form.serverIp} onChange={e => set('serverIp', e.target.value.trim())}
                      placeholder="Auto-detect" className={`${inputCls()} font-mono text-xs`} />
                  </Field>
                </div>

                <div className="mt-5 pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div className={`flex items-center gap-2.5 text-xs ${session.active ? 'text-emerald-700' : 'text-slate-500'}`}>
                    <span className={`w-2 h-2 rounded-full ${session.active ? 'bg-emerald-500' : session.status === 'EXPIRED' ? 'bg-red-400' : 'bg-slate-300'}`} />
                    {session.active
                      ? <>GSTN session active{session.expiresAt && <> · expires {new Date(session.expiresAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</>}</>
                      : session.status === 'EXPIRED' ? 'GSTN session expired — authenticate again' : 'Not authenticated with GSTN'}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {credsDirty && (
                      <span className="text-[11px] text-amber-700">Save to use the new credentials</span>
                    )}
                    <button type="button" onClick={handleVerifyGstin} disabled={verifyingGstin}
                      className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors">
                      {verifyingGstin ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-search-eye-line" />}
                      Verify GSTIN
                    </button>
                    <button type="button" onClick={openAuth}
                      className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 cursor-pointer whitespace-nowrap transition-colors">
                      <i className="ri-shield-keyhole-line" /> {session.active ? 'Re-authenticate' : 'Authenticate with GSTN'}
                    </button>
                  </div>
                </div>
              </Card>

              {/* Notifications */}
              <Card id="alerts" icon="ri-notification-3-line" title="Notifications"
                subtitle="Who should hear about deposit deadlines and failed deductions"
                right={<Toggle checked={form.emailAlertsEnabled} onChange={() => set('emailAlertsEnabled', !form.emailAlertsEnabled)} />}>
                {form.emailAlertsEnabled ? (
                  <Field label="Recipient emails" error={showErr('emailRecipients')} hint="Separate multiple addresses with commas">
                    <input type="text" value={form.emailRecipients} onChange={e => set('emailRecipients', e.target.value)}
                      placeholder="accounts@yourcompany.com, admin@yourcompany.com"
                      className={inputCls(showErr('emailRecipients'))} />
                    {recipients.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {recipients.map(r => (
                          <span key={r} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${EMAIL_RE.test(r) ? 'bg-indigo-50 text-indigo-700 border-indigo-100' : 'bg-red-50 text-red-700 border-red-200'}`}>
                            <i className="ri-mail-line" /> {r}
                          </span>
                        ))}
                      </div>
                    )}
                  </Field>
                ) : (
                  <p className="text-sm text-slate-500 flex items-center gap-2">
                    <i className="ri-notification-off-line text-slate-400" /> Email alerts are off.
                  </p>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>

      {showOtpModal && (
        <OtpAuthModal
          onClose={() => setShowOtpModal(false)}
          onAuthenticated={(expiresAt) => {
            setSession({ status: 'ACTIVE', active: true, expiresAt });
            toast.success('Authenticated with GSTN ✓');
          }}
        />
      )}
    </AppLayout>
  );
}
